import { MyPageCard } from './MyPageCard';
import { isCouponAvailable } from '@/api';
import calendarIcon from '@/assets/icons/icon-calendar.png';
import csIcon from '@/assets/icons/icon-cs.png';
import listIcon from '@/assets/icons/icon-list.png';
import { ClientAuth, Device, Sound } from '@/classes';
import { SECOND_MS } from '@/constants';
import { PopupContext } from '@/contexts/PopupContext';
import { ChangeLanguageModalContent } from '@/features/change-language';
import { EncourageSubscribeBanner, SubscriptionADCard } from '@/features/subscribe';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { useRouter } from '@/hooks/useRouter';
import { ConfirmModalContent } from '@/shared/ui';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import i18n from 'i18next';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Toastify from 'toastify-js';

const IMAGE_SIZE = 'w-20 h-20';

const LanguageChangeButton = () => {
  const { t } = useTranslation();
  const popupContext = useContext(PopupContext);
  const { ref, focused, focusSelf } = useExtendFocusable();

  const onClick = useCallback(() => {
    Sound.press();
    if (popupContext) {
      popupContext.setPopupData((prev) => {
        if (!prev.open) {
          const onChange = async (language: string) => {
            Device.language = language;
            i18n.changeLanguage(language);
            popupContext.setPopupData({ open: false, onClose: () => {}, children: null });
            focusSelf();
          };

          const onClose = () => {
            popupContext.setPopupData({ open: false, onClose: () => {}, children: null });
            focusSelf();
          };

          return {
            open: true,
            onClose: () => {},
            children: <ChangeLanguageModalContent onChange={onChange} onClose={onClose} />,
          };
        } else {
          return prev;
        }
      });
    }
  }, [focusSelf, popupContext]);

  useFocusElement({ ref, focused, onClick, keyCode: 13 });

  return (
    <button
      ref={ref}
      className={`mx-[0.5rem] mt-[40px] px-[60px] h-[79px] bg-white border-primary-2 rounded-[20px] py-[4px] text-[30px] font-bold text-[#2B2B2B] focus:text-[#FFFFFF] hover:text-[#FFFFFF] focus:bg-primary hover:bg-primary outline-focus-none`}
      onClick={onClick}
      onContextMenu={onClick}
    >
      {t('player_language_button')}
    </button>
  );
};

const LogoutButton = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const popupContext = useContext(PopupContext);
  const { ref, focused, focusSelf } = useExtendFocusable();

  const onClickLogout = useCallback(() => {
    Sound.press();
    if (popupContext) {
      popupContext.setPopupData((prev) => {
        if (!prev.open) {
          const onConfirm = async () => {
            await ClientAuth.logout();
            router.push('/', { replace: true });
            popupContext.setPopupData({ open: false, onClose: () => {}, children: null });
          };

          const onCancel = () => {
            popupContext.setPopupData({ open: false, onClose: () => {}, children: null });
            focusSelf();
          };

          return {
            open: true,
            onClose: () => {},
            children: (
              <ConfirmModalContent
                message={t('caution_sign_out')}
                confirmMessage={t('button_sign_out')}
                cancelMessage={t('button_cancel')}
                onConfirm={onConfirm}
                onCancel={onCancel}
              />
            ),
          };
        } else {
          return prev;
        }
      });
    }
  }, [focusSelf, router, popupContext, t]);

  useFocusElement({ ref, focused, onClick: onClickLogout, keyCode: 13 });

  return (
    <button
      ref={ref}
      className={`mx-[0.5rem] mt-[40px] px-[60px] h-[79px] bg-[#E2E2E2] border-primary-2 rounded-[20px] py-[4px] text-[30px] font-bold text-[#2B2B2B] focus:text-[#FFFFFF] hover:text-[#FFFFFF] focus:bg-primary hover:bg-primary outline-focus-none`}
      onClick={onClickLogout}
      onContextMenu={onClickLogout}
    >
      {t('button_sign_out')}
    </button>
  );
};

export const MyProfile = () => {
  const popupContext = useContext(PopupContext);
  const { ref, focusKey } = useExtendFocusable({
    focusable: true,
    saveLastFocusedChild: false,
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: false,
  });
  const scrollingRef = useRef<null | HTMLDivElement>(null);
  const { t } = useTranslation();

  const eventToast = Toastify({
    text: t('t_event_coming_soon'),
    duration: 5 * SECOND_MS,
    newWindow: false,
    gravity: 'bottom',
    position: 'center',
    stopOnFocus: false,
    offset: {
      x: 0,
      y: 40,
    },
    style: {
      textAlign: 'center',
      color: '#FFFFFF',
      background: '#0F2648',
      fontSize: '36px',
      fontWeight: '700',
      padding: '25px 80px',
      borderRadius: '20px',
      minWidth: '85vw',
      maxWidth: '85vw',
    },
  });

  useEffect(() => {
    return () => {
      if (eventToast.toastElement) eventToast.hideToast();
    };
  }, [eventToast]);

  const handleEventCardClick = useCallback(async () => {
    if (Device.isTizen && ClientAuth.user && !ClientAuth.user.isSubscriber) {
      // ! Tizen Promotion 기간동안만 진행 (추후 전체 제거 PopupContext 도 제거 필요)
      const promotionCode = 'SAMSUNG2025';
      try {
        const isAvailablePromotionCode = await isCouponAvailable(promotionCode);
        if (isAvailablePromotionCode) {
          popupContext.showSubscribePopup('mypage', focusKey);
          return;
        } else {
          throw new Error('Promotion code is not available');
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (eventToast.toastElement) {
      eventToast.hideToast();
    }

    eventToast.showToast();
  }, [eventToast, focusKey, popupContext]);

  return (
    <div className="flex flex-[1_0_0] overflow-hidden">
      <main
        id="main-container"
        ref={scrollingRef}
        className="w-full h-screen overflow-x-hidden overflow-y-auto bg-[#F1FDFF]"
      >
        <FocusContext.Provider value={focusKey}>
          <div ref={ref}>
            <EncourageSubscribeBanner />
            <div className="pt-[38px] px-[65px] px-auto flex flex-col h-full flex-0 relative items-end w-full">
              <div className="flex-row flex w-full">
                <SubscriptionADCard
                  focusKey="subscribeVideo"
                  onFocused={() => {}}
                  tabIndex={0}
                  title={t('mypage_video_title')}
                />

                <MyPageCard
                  icon={<img className={IMAGE_SIZE} src={calendarIcon} />}
                  title={
                    <div>
                      <span className="rounded-full text-white bg-[#969696] px-[12px] py-[7px] text-[18px]">
                        {t('mypage_menu_event_coming_soon')}
                      </span>
                      <p className="text-[36px] font-bold text-black flex items-center h-[84px] leading-10">
                        {t('mypage_menu_event')}
                      </p>
                    </div>
                  }
                  navigateTo="/mypage"
                  onClick={handleEventCardClick}
                />

                <MyPageCard
                  icon={<img className={IMAGE_SIZE} src={listIcon} />}
                  title={t('mypage_menu_terms')}
                  navigateTo="/terms"
                />
                <MyPageCard
                  icon={<img className={IMAGE_SIZE} src={csIcon} />}
                  title={t('mypage_menu_support')}
                  navigateTo="/cs"
                />
              </div>

              <div className="flex">
                <LanguageChangeButton />
                <LogoutButton />
              </div>
            </div>
          </div>
        </FocusContext.Provider>
      </main>
    </div>
  );
};
